import {
  useFetchApi,
  useFetchApiAndTransform,
  UseFetchDataResponse,
  useMultipleFetchApi
} from "./useFetchApi";
import { passThroughQueryString } from "./localization/geolocation/geolocationLoader";
import { WindowLocation } from "@reach/router";
import { OffersUrlsQueryQuery } from "types/graphql-types";
import { removeDraft } from "./routing/pathsCache";
import { graphql, useStaticQuery } from "gatsby";
import { Language } from "./localization/languages/types";
import {
  OfferFromApi,
  OffersFromApi,
  TermsAndConditionsFromApi
} from "types/api/exclusive_offers.d";
import { LocationSpecificOffer } from "components/useLocationSpecificOffers/types";
import { offersToLocationSpecificOffers } from "components/useLocationSpecificOffers/helpers";

export type RemoteOffersResponse = UseFetchDataResponse<
  LocationSpecificOffer[]
>;
export type RemoteOfferResponse = UseFetchDataResponse<OfferFromApi>;
export type RemoteOfferResponses = UseFetchDataResponse<OfferFromApi[]>;
export type RemoteTermsAndConditionsResponse = UseFetchDataResponse<
  TermsAndConditionsFromApi[]
>;

const apiURL = `${process.env.GATSBY_DIGITAL_API_BASE_URL}/exclusiveOffers`;

const useRemoteOffers = (
  language: Language,
  location: WindowLocation | null
): RemoteOffersResponse => {
  const offersPathMapping = useOffersPaths(language);

  return useFetchApiAndTransform<OffersFromApi[], LocationSpecificOffer[]>(
    `${apiURL}${passThroughQueryString(location, language)}`,
    offersToLocationSpecificOffers(language, offersPathMapping)
  );
};

const useRemoteOffer = (
  sanityOfferId: string | undefined,
  location: WindowLocation | null,
  language: Language
): RemoteOfferResponse =>
  useFetchApi<OfferFromApi>(
    sanityOfferId
      ? `${apiURL}/${removeDraft(sanityOfferId)}${passThroughQueryString(
          location,
          language
        )}`
      : undefined
  );

const useTermsAndConditions = (
  location: WindowLocation | null,
  language: Language
): RemoteTermsAndConditionsResponse =>
  useFetchApi<TermsAndConditionsFromApi[]>(
    `${
      process.env.GATSBY_DIGITAL_API_BASE_URL
    }/termsAndConditions${passThroughQueryString(location, language)}`
  );

const useRemoteOfferByIds = (
  sanityOfferIds: string[],
  location: WindowLocation | null,
  language: Language
): RemoteOfferResponses =>
  useMultipleFetchApi<OfferFromApi>(
    sanityOfferIds.map(
      (x) =>
        `${apiURL}/${removeDraft(x)}${passThroughQueryString(
          location,
          language
        )}`
    )
  );

export type OffersPaths = { [promoId: string]: string };

const useOffersPaths = (language: Language): OffersPaths => {
  const {
    offersPerPromo,
    offersPerAirPromo
  } = useStaticQuery<OffersUrlsQueryQuery>(graphql`
    query OffersUrlsQuery {
      offersPerPromo: allSanityExclusiveOfferPerPromo(
        filter: {
          metadata: { routing: { slug: { en: { current: { ne: null } } } } }
        }
      ) {
        nodes {
          _id
          i18nFullPaths
        }
      }

      offersPerAirPromo: allSanityExclusiveOfferPerAirPromo(
        filter: {
          metadata: { routing: { slug: { en: { current: { ne: null } } } } }
        }
      ) {
        nodes {
          _id
          i18nFullPaths
        }
      }
    }
  `);

  const pathMapping = [
    ...offersPerPromo.nodes,
    ...offersPerAirPromo.nodes
  ].reduce<OffersPaths>((acc, promo) => {
    if (promo._id)
      acc[removeDraft(promo._id)] = promo.i18nFullPaths[language.code];
    return acc;
  }, {});

  return pathMapping;
};

export {
  useRemoteOfferByIds,
  useRemoteOffer,
  useRemoteOffers,
  useOffersPaths,
  useTermsAndConditions
};
